/**
 * Retiro en otra sucursal (Fase 4 de retiros, respuestas de la dueña y la contadora del 2026-10-29).
 *  - En la sucursal de VENTA: «Otra sucursal» en Por entregar crea la orden de retiro (almacén de otra sucursal,
 *    productos y cantidades, quién retirará). La autoriza la dueña o quien tenga su permiso; si no, se pide la
 *    autorización con clave o a distancia. El código de 6 dígitos se muestra UNA vez y va al correo del cliente.
 *  - En la sucursal de RETIRO: «Para retirar aquí desde otra sucursal» con las órdenes abiertas; «Registrar retiro»
 *    pide el código y la cédula y deja la entrega y la guía de traslado, que se imprimen desde la ventana fija.
 *  - La pestaña «Órdenes de retiro» lista las de las dos sucursales con su estado; la de venta extiende (7 días más, con
 *    justificación), anula (con motivo) o da un código nuevo.
 */
import { ESTADO_ORDEN_RETIRO_ETIQUETA, formatearCantidad, Money, type CorreoAutomaticoDTO, type EstadoOrdenRetiro, type OrdenRetiroCreadaDTO, type OrdenRetiroDTO } from '@mg/shared';
import { ArrowRightLeft, KeyRound, PackageCheck, Printer } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { AyudaVentana } from '@/ayuda/BotonAyuda.tsx';
import { useAutorizacion } from '@/componentes/Autorizacion.tsx';
import { mostrarRegistrado } from '@/componentes/Registrado.tsx';
import { EstadoQuery, Vacio } from '@/componentes/estados.tsx';
import { Badge } from '@/componentes/ui/badge.tsx';
import { Button } from '@/componentes/ui/button.tsx';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/componentes/ui/dialog.tsx';
import { Input, Select } from '@/componentes/ui/input.tsx';
import { Campo } from '@/componentes/ui/label.tsx';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/componentes/ui/table.tsx';
import { mensajeDeError } from '@/lib/api.ts';
import { normalizarDecimal, parseMonto } from '@/lib/montos.ts';
import { formatearFechaHora } from '@/lib/utils.ts';
import { useAnularOrdenRetiro, useCrearOrdenRetiro, useExtenderOrdenRetiro, useNuevoCodigoRetiro, useOpcionesRetiro, useOrdenesRetiro, useRetirarOtraSucursal } from './api.ts';

const cant = (v: string) => formatearCantidad(Money.parse(v));
const nombreVenta = (o: { documento_tipo: string; documento_numero: string }) => `${o.documento_tipo === 'FAC' ? 'Factura' : 'Nota de entrega'} N.º ${o.documento_numero}`;
const VARIANTE: Record<EstadoOrdenRetiro, 'info' | 'pendiente' | 'ok' | 'critico' | 'neutro'> = { activa: 'info', parcial: 'pendiente', retirada: 'ok', vencida: 'critico', anulada: 'neutro' };
/** Qué pasó con el correo de la orden, para la ventana que queda en pantalla (resaltado si no salió). */
const renglonCorreo = (c: CorreoAutomaticoDTO | null | undefined) => (c && c.estado !== 'no_aplica' ? [{ texto: c.mensaje, aviso: c.estado !== 'enviado' }] : []);
const pendienteTotal = (o: OrdenRetiroDTO) => o.lineas.reduce((s, l) => s.add(Money.parse(l.pendiente)), Money.cero());

export function BadgeOrdenRetiro({ orden }: { orden: OrdenRetiroDTO }) {
  return (
    <span className="inline-flex flex-wrap gap-1">
      <Badge variant={VARIANTE[orden.estado]} data-testid={`orden-estado-${orden.numero_formateado}`}>{ESTADO_ORDEN_RETIRO_ETIQUETA[orden.estado]}</Badge>
      {orden.vencida_sin_procesar ? <Badge variant="critico">Vencida: vuelve a {orden.sucursal_nombre}</Badge> : null}
      {orden.codigo_bloqueado && (orden.estado === 'activa' || orden.estado === 'parcial') ? <Badge variant="critico">Código bloqueado</Badge> : null}
    </span>
  );
}

/** El código, una sola vez: después solo existe su hash. */
function VistaCodigo({ creada }: { creada: OrdenRetiroCreadaDTO }) {
  return (
    <div className="space-y-3" data-testid="orden-codigo-ventana">
      {creada.codigo ? (
        <div className="rounded-2xl border-2 border-mg-verde bg-mg-verde-claro/40 p-4 text-center">
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Código de retiro</p>
          <p className="num font-mono text-4xl font-bold tracking-[0.35em]" data-testid="orden-codigo">{creada.codigo}</p>
          <p className="mt-1 text-xs text-muted-foreground">Vence el {formatearFechaHora(creada.vence_en)}</p>
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">La orden ya estaba hecha: el código se mostró la primera vez. Si se perdió, usa <b>Código nuevo</b>.</p>
      )}
      {creada.codigo ? <p className="rounded-lg border border-mg-ambar bg-amber-50 px-3 py-2 text-xs font-medium text-amber-900">Dáselo al cliente: no se vuelve a mostrar. En {creada.sucursal_retiro_nombre} le pedirán este código y la cédula de quien retira.</p> : null}
      {renglonCorreo(creada.correo_orden).map((r) => (
        <p key={r.texto} className={r.aviso ? 'rounded-lg border border-mg-ambar bg-amber-50 px-3 py-1.5 text-xs font-medium text-amber-900' : 'text-sm text-muted-foreground'} data-testid="orden-codigo-correo">{r.texto}</p>
      ))}
    </div>
  );
}

// ─── Crear la orden (sucursal de venta) ─────────────────────────────────────
export function DialogoOrdenRetiro({ documentoId, cerrar }: { documentoId: string; cerrar: () => void }) {
  const op = useOpcionesRetiro(documentoId);
  const crear = useCrearOrdenRetiro();
  const aut = useAutorizacion();
  // Lo elegido a mano; mientras no se toque, el primer almacén y todo lo pendiente de cada renglón
  const [almacenElegido, setAlmacenId] = useState('');
  const [editadas, setCantidades] = useState<Record<string, string>>({});
  const [nombre, setNombre] = useState('');
  const [cedula, setCedula] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [creada, setCreada] = useState<OrdenRetiroCreadaDTO | null>(null);
  const almacenId = almacenElegido || op.data?.almacenes[0]?.id || '';
  const cantidades: Record<string, string> = { ...Object.fromEntries((op.data?.lineas ?? []).filter((l) => !l.sin_inventario).map((l) => [l.documento_linea_id, l.pendiente])), ...editadas };
  const almacen = op.data?.almacenes.find((a) => a.id === almacenId);
  const lineas = Object.entries(cantidades).filter(([, v]) => parseMonto(v)?.esPositivo()).map(([documento_linea_id, cantidad]) => ({ documento_linea_id, cantidad: normalizarDecimal(cantidad) }));
  const confirmar = async () => {
    if (!op.data || !almacen) return;
    setEnviando(true);
    try {
      const datos = { almacen_retiro_id: almacen.id, lineas, ...(nombre.trim() ? { retira_nombre: nombre.trim() } : {}), ...(cedula.trim() ? { retira_cedula: cedula.trim() } : {}) };
      const r = await aut.ejecutar((ids) => crear.mutateAsync({ documentoId, datos: { ...datos, ...(ids.length ? { autorizaciones: ids } : {}) } }), {
        motivo: `Retiro en ${almacen.sucursal_nombre}`,
        detalle: { documento: `${op.data.documento_tipo} ${op.data.documento_numero}`, cliente: op.data.cliente_razon_social, retira_en: `${almacen.sucursal_nombre} / ${almacen.nombre}` },
      });
      if (r) setCreada(r);
    } catch (e) {
      toast.error('No se pudo hacer la orden de retiro', { description: mensajeDeError(e) });
    } finally {
      setEnviando(false);
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && !enviando && cerrar()}>
      <DialogContent className="max-w-2xl" data-testid="dialogo-orden-retiro">
        <DialogHeader>
          <div className="flex items-center gap-1"><DialogTitle>{creada ? `Orden de retiro ${creada.numero_formateado}` : 'Retirar en otra sucursal'}</DialogTitle><AyudaVentana tema="entregas.orden-retiro" /></div>
          <DialogDescription>
            {creada
              ? `${nombreVenta(creada)} · ${creada.cliente_razon_social} · se retira en ${creada.sucursal_retiro_nombre} / ${creada.almacen_retiro_nombre}`
              : 'Lo indicado queda apartado en la otra sucursal y el cliente lo retira allá con el código y su cédula. Vale 7 días.'}
          </DialogDescription>
        </DialogHeader>
        {creada ? (
          <VistaCodigo creada={creada} />
        ) : (
          <EstadoQuery cargando={op.isLoading} error={op.error} datos={op.data} reintentar={() => void op.refetch()}>
            {(d) =>
              d.orden_abierta ? (
                <p className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-900" data-testid="orden-retiro-ya-hay">Esta venta ya tiene la orden de retiro {d.orden_abierta.numero_formateado} para retirar en {d.orden_abierta.sucursal_retiro_nombre}. Para cambiarla, anúlala en la pestaña <b>Órdenes de retiro</b>.</p>
              ) : d.almacenes.length === 0 ? (
                <p className="text-sm text-muted-foreground">No hay almacenes activos en otras sucursales.</p>
              ) : (
                <>
                  <p className="text-sm">{d.documento_tipo} {d.documento_numero} · {d.cliente_razon_social}</p>
                  <div className="grid grid-cols-2 gap-3">
                    <Campo etiqueta="Retira en" htmlFor="ort-almacen" className="col-span-2">
                      <Select id="ort-almacen" value={almacenId} onChange={(e) => setAlmacenId(e.target.value)} data-testid="orden-retiro-almacen">
                        {d.almacenes.map((a) => <option key={a.id} value={a.id}>{a.sucursal_nombre} / {a.nombre}</option>)}
                      </Select>
                    </Campo>
                    <Campo etiqueta="Quién retira (opcional)" htmlFor="ort-nombre">
                      <Input id="ort-nombre" value={nombre} onChange={(e) => setNombre(e.target.value)} maxLength={120} data-testid="orden-retiro-nombre" />
                    </Campo>
                    <Campo etiqueta="Su cédula (opcional: si la pones, se exige)" htmlFor="ort-cedula">
                      <Input id="ort-cedula" value={cedula} onChange={(e) => setCedula(e.target.value)} placeholder="V-12345678" maxLength={40} data-testid="orden-retiro-cedula" />
                    </Campo>
                  </div>
                  {almacen?.direccion ? <p className="text-xs text-muted-foreground">Dirección: {almacen.direccion}</p> : null}
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Producto</TableHead>
                        <TableHead className="text-right">Pendiente</TableHead>
                        <TableHead className="text-right">Disponible allá</TableHead>
                        <TableHead className="text-right">Retira allá</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {d.lineas.map((l) => {
                        const disp = almacen?.disponibles[l.producto_id];
                        const falta = !l.sin_inventario && disp !== undefined && parseMonto(cantidades[l.documento_linea_id] ?? '')?.gt(Money.parse(disp));
                        return (
                          <TableRow key={l.documento_linea_id}>
                            <TableCell>
                              <p className="font-medium">{l.producto_nombre}</p>
                              <p className="text-[11px] text-muted-foreground">{l.producto_codigo}{l.almacen_nombre ? ` · apartado en ${l.almacen_nombre}` : ''}{l.sin_inventario ? ' · pieza o producto con detalle: se entrega aquí' : ''}</p>
                            </TableCell>
                            <TableCell className="text-right num">{cant(l.pendiente)}</TableCell>
                            <TableCell className={`text-right num ${falta ? 'font-semibold text-mg-rojo' : ''}`} data-testid={`orden-retiro-disponible-${l.producto_codigo}`}>{l.sin_inventario || disp === undefined ? '—' : cant(disp)}</TableCell>
                            <TableCell className="text-right">
                              {l.sin_inventario ? '—' : <Input inputMode="decimal" value={cantidades[l.documento_linea_id] ?? ''} onChange={(e) => setCantidades((c) => ({ ...c, [l.documento_linea_id]: e.target.value }))} className="h-8 w-24 text-right" aria-label={`Retira allá ${l.producto_codigo}`} data-testid={`orden-retiro-cantidad-${l.producto_codigo}`} />}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </>
              )
            }
          </EstadoQuery>
        )}
        <DialogFooter>
          {creada ? (
            <Button onClick={cerrar} data-testid="orden-retiro-listo">Listo</Button>
          ) : (
            <>
              <Button variant="outline" onClick={cerrar} disabled={enviando}>Cancelar</Button>
              <Button onClick={() => void confirmar()} cargando={enviando} disabled={!almacen || lineas.length === 0 || !!op.data?.orden_abierta || enviando} data-testid="confirmar-orden-retiro"><ArrowRightLeft /> Hacer la orden de retiro</Button>
            </>
          )}
        </DialogFooter>
        {aut.dialogo}
      </DialogContent>
    </Dialog>
  );
}

// ─── Registrar el retiro (sucursal de retiro) ───────────────────────────────
export function DialogoRetiroOtraSucursal({ orden, cerrar }: { orden: OrdenRetiroDTO; cerrar: () => void }) {
  const retirar = useRetirarOtraSucursal();
  const aut = useAutorizacion();
  const [codigo, setCodigo] = useState('');
  const [cedula, setCedula] = useState('');
  const [recibe, setRecibe] = useState(orden.retira_nombre ?? orden.cliente_razon_social);
  const [observaciones, setObservaciones] = useState('');
  const [cantidades, setCantidades] = useState<Record<string, string>>(() => Object.fromEntries(orden.lineas.filter((l) => Money.parse(l.pendiente).esPositivo()).map((l) => [l.documento_linea_id, l.pendiente])));
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const lineas = Object.entries(cantidades).filter(([, v]) => parseMonto(v)?.esPositivo()).map(([documento_linea_id, cantidad]) => ({ documento_linea_id, cantidad: normalizarDecimal(cantidad) }));
  const listo = /^\d{6}$/.test(codigo.trim()) && cedula.trim().length >= 3 && lineas.length > 0;
  const confirmar = async () => {
    setError(null);
    setEnviando(true);
    try {
      const datos = { codigo: codigo.trim(), cedula: cedula.trim(), ...(recibe.trim() ? { recibe_nombre: recibe.trim() } : {}), ...(observaciones.trim() ? { observaciones: observaciones.trim() } : {}), lineas };
      const r = await aut.ejecutar((ids) => retirar.mutateAsync({ ordenId: orden.id, datos: { ...datos, ...(ids.length ? { autorizaciones: ids } : {}) } }), {
        motivo: 'Entregar con la retención por recibir',
        detalle: { documento: `${orden.documento_tipo} ${orden.documento_numero}`, cliente: orden.cliente_razon_social, orden: orden.numero_formateado },
      });
      if (!r) return;
      cerrar();
      const ret = r.retiro_otra_sucursal;
      mostrarRegistrado({
        titulo: `Retiro registrado: entrega ${r.numero_formateado}`,
        descripcion: `${nombreVenta(orden)} de ${orden.sucursal_nombre}, retirada en ${orden.sucursal_retiro_nombre} · ${r.cliente_razon_social}`,
        detalle: [
          { texto: r.orden.estado === 'retirada' ? 'La orden quedó retirada por completo.' : 'Queda algo por retirar con esta orden: el cliente recibe un código nuevo en su correo (o se lo da la sucursal de venta).' },
          ...(ret ? [{ texto: `Guía de traslado ${ret.traslado_numero}: de ${ret.almacen_retiro_nombre} a ${ret.sucursal_venta_nombre}, sin factura.` }] : []),
          ...renglonCorreo(r.correo_retiro),
          ...renglonCorreo(r.correo_codigo),
        ],
        imprimir: [{ etiqueta: 'Imprimir despacho', ruta: `/imprimir/ENT/${r.id}` }, ...(ret ? [{ etiqueta: 'Imprimir guía de traslado', ruta: `/imprimir/TRAS/${ret.traslado_id}` }] : [])],
      });
    } catch (e) {
      // El código o la cédula equivocados se dicen aquí mismo, con los intentos que quedan
      setError(mensajeDeError(e));
    } finally {
      setEnviando(false);
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && !enviando && cerrar()}>
      <DialogContent className="max-w-2xl" data-testid="dialogo-retiro-otra-sucursal">
        <DialogHeader>
          <div className="flex items-center gap-1"><DialogTitle>Registrar retiro · orden {orden.numero_formateado}</DialogTitle><AyudaVentana tema="entregas.retiro-aqui" /></div>
          <DialogDescription>{nombreVenta(orden)} de {orden.sucursal_nombre} · {orden.cliente_razon_social} · {orden.almacen_retiro_nombre}</DialogDescription>
        </DialogHeader>
        {orden.retira_nombre || orden.retira_cedula ? <p className="text-sm" data-testid="retiro-autorizado-a">Autorizado a retirar: <b>{orden.retira_nombre ?? ''}</b>{orden.retira_cedula ? ` (C.I. ${orden.retira_cedula})` : ''}</p> : null}
        <div className="grid grid-cols-2 gap-3">
          <Campo etiqueta="Código de retiro (6 dígitos)" htmlFor="ret-codigo">
            <Input id="ret-codigo" inputMode="numeric" autoComplete="off" maxLength={6} value={codigo} onChange={(e) => setCodigo(e.target.value.replace(/\D/g, ''))} className="num font-mono tracking-[0.3em]" data-testid="retiro-codigo" />
          </Campo>
          <Campo etiqueta="Cédula de quien retira" htmlFor="ret-cedula">
            <Input id="ret-cedula" value={cedula} onChange={(e) => setCedula(e.target.value)} placeholder="V-12345678" maxLength={40} data-testid="retiro-cedula" />
          </Campo>
          <Campo etiqueta="Recibe" htmlFor="ret-recibe">
            <Input id="ret-recibe" value={recibe} onChange={(e) => setRecibe(e.target.value)} maxLength={120} />
          </Campo>
          <Campo etiqueta="Observaciones" htmlFor="ret-obs">
            <Input id="ret-obs" value={observaciones} onChange={(e) => setObservaciones(e.target.value)} placeholder="Placa del vehículo, chofer…" maxLength={500} />
          </Campo>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Producto</TableHead>
              <TableHead className="text-right">Autorizado</TableHead>
              <TableHead className="text-right">Ya retirado</TableHead>
              <TableHead className="text-right">Retira ahora</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {orden.lineas.filter((l) => Money.parse(l.pendiente).esPositivo()).map((l) => (
              <TableRow key={l.documento_linea_id}>
                <TableCell><p className="font-medium">{l.producto_nombre}</p><p className="text-[11px] text-muted-foreground">{l.producto_codigo} · hasta {cant(l.pendiente)} {l.unidad}</p></TableCell>
                <TableCell className="text-right num">{cant(l.cantidad)}</TableCell>
                <TableCell className="text-right num">{cant(l.retirado)}</TableCell>
                <TableCell className="text-right"><Input inputMode="decimal" value={cantidades[l.documento_linea_id] ?? ''} onChange={(e) => setCantidades((c) => ({ ...c, [l.documento_linea_id]: e.target.value }))} className="h-8 w-24 text-right" aria-label={`Retira ahora ${l.producto_codigo}`} data-testid={`retiro-cantidad-${l.producto_codigo}`} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {error ? <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-900" role="alert" data-testid="retiro-error">{error}</p> : null}
        <DialogFooter>
          <Button variant="outline" onClick={cerrar} disabled={enviando}>Cancelar</Button>
          <Button onClick={() => void confirmar()} cargando={enviando} disabled={!listo || enviando} data-testid="confirmar-retiro-otra-sucursal"><PackageCheck /> Registrar retiro</Button>
        </DialogFooter>
        {aut.dialogo}
      </DialogContent>
    </Dialog>
  );
}

/** Por qué no se puede retirar todavía (lo dice la fila en lugar del botón). */
const motivoNoRetira = (o: OrdenRetiroDTO) => (o.vencida_sin_procesar ? `Venció: ${o.sucursal_nombre} puede extenderla` : o.codigo_bloqueado ? `Código bloqueado: ${o.sucursal_nombre} da uno nuevo` : null);

/** Sección de Por entregar en la sucursal de retiro: lo que viene a retirar aquí gente que compró en otra sucursal. */
export function ParaRetirarAqui() {
  const ordenes = useOrdenesRetiro({ ver: 'retiro', abiertas: true, limite: 50 });
  const [retirando, setRetirando] = useState<OrdenRetiroDTO | null>(null);
  const datos = ordenes.data?.datos ?? [];
  if (datos.length === 0) return null;
  return (
    <section className="mb-4 rounded-2xl border border-blue-200 bg-blue-50/40 p-3" data-testid="para-retirar-aqui">
      <h2 className="mb-2 text-sm font-semibold">Para retirar aquí desde otra sucursal ({datos.length})</h2>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Orden</TableHead>
            <TableHead>Venta</TableHead>
            <TableHead>Cliente / retira</TableHead>
            <TableHead>Almacén</TableHead>
            <TableHead>Vence</TableHead>
            <TableHead className="text-right">Por retirar</TableHead>
            <TableHead />
          </TableRow>
        </TableHeader>
        <TableBody>
          {datos.map((o) => (
            <TableRow key={o.id} data-testid={`retirar-aqui-${o.numero_formateado}`}>
              <TableCell className="font-medium">{o.numero_formateado}<div><BadgeOrdenRetiro orden={o} /></div></TableCell>
              <TableCell>{nombreVenta(o)}<p className="text-[11px] text-muted-foreground">de {o.sucursal_nombre}</p></TableCell>
              <TableCell>{o.cliente_razon_social}{o.retira_nombre || o.retira_cedula ? <p className="text-[11px] text-muted-foreground">Retira {o.retira_nombre ?? ''}{o.retira_cedula ? ` (C.I. ${o.retira_cedula})` : ''}</p> : null}</TableCell>
              <TableCell>{o.almacen_retiro_nombre}</TableCell>
              <TableCell>{formatearFechaHora(o.vence_en)}</TableCell>
              <TableCell className="text-right num">{formatearCantidad(pendienteTotal(o))}</TableCell>
              <TableCell className="text-right">
                {o.puede_retirar ? <Button size="sm" onClick={() => setRetirando(o)} data-testid={`registrar-retiro-${o.numero_formateado}`}><PackageCheck /> Registrar retiro</Button> : <span className="text-xs text-mg-rojo">{motivoNoRetira(o)}</span>}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {retirando ? <DialogoRetiroOtraSucursal orden={retirando} cerrar={() => setRetirando(null)} /> : null}
    </section>
  );
}

// ─── Gestionar la orden (sucursal de venta) ─────────────────────────────────
type Accion = 'extender' | 'anular' | 'codigo';
function DialogoGestionOrden({ orden, accion, cerrar }: { orden: OrdenRetiroDTO; accion: Accion; cerrar: () => void }) {
  const extender = useExtenderOrdenRetiro();
  const anular = useAnularOrdenRetiro();
  const codigo = useNuevoCodigoRetiro();
  const aut = useAutorizacion();
  const [texto, setTexto] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [creada, setCreada] = useState<OrdenRetiroCreadaDTO | null>(null);
  const titulo = { extender: 'Extender la orden', anular: 'Anular la orden', codigo: 'Código nuevo' }[accion];
  const confirmar = async () => {
    setEnviando(true);
    const motivo = { extender: `Extender la orden ${orden.numero_formateado}`, anular: `Anular la orden ${orden.numero_formateado}`, codigo: `Código nuevo para la orden ${orden.numero_formateado}` }[accion];
    const detalle = { documento: `${orden.documento_tipo} ${orden.documento_numero}`, cliente: orden.cliente_razon_social, retira_en: orden.sucursal_retiro_nombre };
    try {
      if (accion === 'extender') {
        const r = await aut.ejecutar((ids) => extender.mutateAsync({ id: orden.id, datos: { justificacion: texto.trim(), ...(ids.length ? { autorizaciones: ids } : {}) } }), { motivo, detalle });
        if (!r) return;
        toast.success(`Orden ${orden.numero_formateado}: vence el ${formatearFechaHora(r.vence_en)}`);
        cerrar();
      } else if (accion === 'anular') {
        const r = await aut.ejecutar((ids) => anular.mutateAsync({ id: orden.id, datos: { motivo: texto.trim(), ...(ids.length ? { autorizaciones: ids } : {}) } }), { motivo, detalle });
        if (!r) return;
        toast.success(`Orden ${orden.numero_formateado} anulada: lo apartado volvió a ${orden.sucursal_nombre}`, { description: r.correo_orden.estado === 'enviado' ? r.correo_orden.mensaje : undefined });
        cerrar();
      } else {
        const r = await aut.ejecutar((ids) => codigo.mutateAsync({ id: orden.id, autorizaciones: ids }), { motivo, detalle });
        if (r) setCreada(r);
      }
    } catch (e) {
      toast.error(`No se pudo: ${titulo.toLowerCase()}`, { description: mensajeDeError(e) });
    } finally {
      setEnviando(false);
    }
  };
  const pideTexto = accion !== 'codigo';
  return (
    <Dialog open onOpenChange={(o) => !o && !enviando && cerrar()}>
      <DialogContent className="max-w-md" data-testid={`dialogo-orden-${accion}`}>
        <DialogHeader>
          <DialogTitle>{titulo} · {orden.numero_formateado}</DialogTitle>
          <DialogDescription>
            {accion === 'extender' ? `Vence el ${formatearFechaHora(orden.vence_en)}: suma 7 días más.` : accion === 'anular' ? `Lo que falta por retirar vuelve a ${orden.sucursal_nombre}, apartado para el cliente.` : 'El código anterior deja de servir y se vuelven a contar los intentos. El nuevo se muestra una vez y va al correo del cliente.'}
          </DialogDescription>
        </DialogHeader>
        {creada ? <VistaCodigo creada={creada} /> : pideTexto ? (
          <Campo etiqueta={accion === 'extender' ? 'Justificación' : 'Motivo'} htmlFor="ort-texto">
            <Input id="ort-texto" value={texto} onChange={(e) => setTexto(e.target.value)} maxLength={500} data-testid="orden-gestion-texto" />
          </Campo>
        ) : null}
        <DialogFooter>
          {creada ? (
            <Button onClick={cerrar}>Listo</Button>
          ) : (
            <>
              <Button variant="outline" onClick={cerrar} disabled={enviando}>Cancelar</Button>
              <Button variant={accion === 'anular' ? 'destructive' : 'default'} onClick={() => void confirmar()} cargando={enviando} disabled={enviando || (pideTexto && texto.trim().length < 5)} data-testid="orden-gestion-confirmar">{titulo}</Button>
            </>
          )}
        </DialogFooter>
        {aut.dialogo}
      </DialogContent>
    </Dialog>
  );
}

/** Pestaña «Órdenes de retiro»: las de las dos sucursales (como venta o como retiro), con su estado y sus acciones. */
export function ListaOrdenesRetiro() {
  const [ver, setVer] = useState<'' | 'venta' | 'retiro'>('');
  const [estado, setEstado] = useState('');
  const ordenes = useOrdenesRetiro({ ver: ver || undefined, estado: estado || undefined, limite: 50 });
  const [gestion, setGestion] = useState<{ orden: OrdenRetiroDTO; accion: Accion } | null>(null);
  const [retirando, setRetirando] = useState<OrdenRetiroDTO | null>(null);
  return (
    <>
      <div className="mb-3 flex flex-wrap gap-2">
        <Select value={ver} onChange={(e) => setVer(e.target.value as typeof ver)} className="w-56" aria-label="Qué órdenes" data-testid="ordenes-ver">
          <option value="">Vendidas aquí y para retirar aquí</option>
          <option value="venta">Vendidas aquí (retiran en otra)</option>
          <option value="retiro">Para retirar aquí</option>
        </Select>
        <Select value={estado} onChange={(e) => setEstado(e.target.value)} className="w-40" aria-label="Estado de la orden" data-testid="ordenes-estado">
          <option value="">Todos los estados</option>
          {Object.entries(ESTADO_ORDEN_RETIRO_ETIQUETA).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </Select>
      </div>
      <EstadoQuery cargando={ordenes.isLoading} error={ordenes.error} datos={ordenes.data} reintentar={() => void ordenes.refetch()}>
        {(d) =>
          d.datos.length === 0 ? (
            <Vacio icono={<ArrowRightLeft className="h-6 w-6" />} titulo="Sin órdenes de retiro" descripcion="Cuando un cliente retire en otra sucursal, la orden sale aquí." />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Orden</TableHead>
                  <TableHead>Venta</TableHead>
                  <TableHead>Cliente</TableHead>
                  <TableHead>Retira en</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead>Vence</TableHead>
                  <TableHead className="text-right">Retirado / autorizado</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {d.datos.map((o) => {
                  const autorizado = o.lineas.reduce((s, l) => s.add(Money.parse(l.cantidad)), Money.cero());
                  const retirado = o.lineas.reduce((s, l) => s.add(Money.parse(l.retirado)), Money.cero());
                  return (
                    <TableRow key={o.id} data-testid={`orden-${o.numero_formateado}`}>
                      <TableCell className="font-medium">{o.numero_formateado}<p className="text-[11px] font-normal text-muted-foreground">{formatearFechaHora(o.creado_en)} · {o.creado_por_nombre}</p></TableCell>
                      <TableCell>{nombreVenta(o)}<p className="text-[11px] text-muted-foreground">de {o.sucursal_nombre}</p></TableCell>
                      <TableCell>{o.cliente_razon_social}</TableCell>
                      <TableCell>{o.sucursal_retiro_nombre}<p className="text-[11px] text-muted-foreground">{o.almacen_retiro_nombre}</p></TableCell>
                      <TableCell>
                        <BadgeOrdenRetiro orden={o} />
                        {o.extensiones.length > 0 ? <p className="text-[11px] text-muted-foreground" title={o.extensiones.map((x) => x.justificacion).join(' · ')}>Extendida {o.extensiones.length === 1 ? '1 vez' : `${o.extensiones.length} veces`}</p> : null}
                        {o.motivo_anulacion ? <p className="text-[11px] text-muted-foreground">{o.motivo_anulacion}</p> : null}
                      </TableCell>
                      <TableCell>{formatearFechaHora(o.vence_en)}</TableCell>
                      <TableCell className="text-right num">{formatearCantidad(retirado)} / {formatearCantidad(autorizado)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex flex-wrap justify-end gap-1">
                          {o.puede_retirar ? <Button size="sm" onClick={() => setRetirando(o)} data-testid={`orden-retirar-${o.numero_formateado}`}><PackageCheck /> Registrar retiro</Button> : null}
                          {o.retiros.map((r) => (
                            <Button key={r.entrega_id} size="sm" variant="outline" onClick={() => window.open(`/imprimir/ENT/${r.entrega_id}`, '_blank')} title={`Guía ${r.traslado_numero}`}><Printer /> {r.entrega_numero}</Button>
                          ))}
                          {o.puede_gestionar ? (
                            <>
                              <Button size="sm" variant="outline" onClick={() => setGestion({ orden: o, accion: 'extender' })} data-testid={`orden-extender-${o.numero_formateado}`}>Extender</Button>
                              <Button size="sm" variant="outline" onClick={() => setGestion({ orden: o, accion: 'codigo' })} data-testid={`orden-codigo-${o.numero_formateado}`}><KeyRound /> Código nuevo</Button>
                              <Button size="sm" variant="outline" onClick={() => setGestion({ orden: o, accion: 'anular' })} data-testid={`orden-anular-${o.numero_formateado}`}>Anular</Button>
                            </>
                          ) : null}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )
        }
      </EstadoQuery>
      {gestion ? <DialogoGestionOrden orden={gestion.orden} accion={gestion.accion} cerrar={() => setGestion(null)} /> : null}
      {retirando ? <DialogoRetiroOtraSucursal orden={retirando} cerrar={() => setRetirando(null)} /> : null}
    </>
  );
}
